import { useEffect } from 'react'

import { fetchLocations } from '../../locations/locationsSlice'
import { fetchUsers } from '../../users/usersSlice'
import { selectLocationsStatus } from '../../locations/locationsSelectors'
import { selectUsersStatus } from '../../users/usersSelectors'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { selectAuditDataStatus } from '../../auditData/auditDataSelectors'
import { fetchAuditData } from '../../auditData/auditDataSlice'
import { fetchAudits, fetchAuditsForDateRange, fetchAuditsForUser } from '../../audits/auditsSlice'
import {
  selectAuditsStatus,
  selectFromDateFilter,
  selectToDateFilter,
} from '../../audits/auditsSelectors'
import { selectUserData } from '../../auth/authSelectors'

const normalizeOptions = (options) => {
  if (options !== null && typeof options === 'object') {
    return {
      filterByDateRange: Boolean(options.filterByDateRange),
      onlyCurrentAuditor: Boolean(options.onlyCurrentAuditor),
    }
  }

  return {
    filterByDateRange: false,
    onlyCurrentAuditor: false,
  }
}

const useFetchAuditsData = (options) => {
  const dispatch = useAppDispatch()
  const { filterByDateRange, onlyCurrentAuditor } = normalizeOptions(options)

  const userData = useAppSelector(selectUserData)
  const auditDataStatus = useAppSelector(selectAuditDataStatus)
  const locationsStatus = useAppSelector(selectLocationsStatus)
  const usersStatus = useAppSelector(selectUsersStatus)
  const loaded = useAppSelector(selectAuditsStatus)
  const fromDateFilter = useAppSelector(selectFromDateFilter)
  const toDateFilter = useAppSelector(selectToDateFilter)
  const lastFetchedDateRangeKey = useAppSelector((s) => s.audits.lastFetchedDateRangeKey)
  const failedDateRangeKey = useAppSelector((s) => s.audits.failedDateRangeKey)

  useEffect(() => {
    const fetchData = async () => {
      if (auditDataStatus === 'idle') dispatch(fetchAuditData())
      if (usersStatus === 'idle') dispatch(fetchUsers())
      if (locationsStatus === 'idle') dispatch(fetchLocations())

      if (filterByDateRange) {
        if (!fromDateFilter || !toDateFilter) return
        const rangeKey = `${fromDateFilter}|${toDateFilter}`
        if (failedDateRangeKey === rangeKey) {
          if (loaded === 'failed') {
            dispatch(fetchAudits())
          }
          return
        }
        if (loaded === 'succeeded' && lastFetchedDateRangeKey === rangeKey) return
        dispatch(fetchAuditsForDateRange({ fromDate: fromDateFilter, toDate: toDateFilter }))
        return
      }

      if (loaded === 'idle') {
        if (onlyCurrentAuditor && userData?.uid) {
          dispatch(fetchAuditsForUser())
        } else {
          dispatch(fetchAudits())
        }
      }
    }
    fetchData()
  }, [
    dispatch,
    filterByDateRange,
    onlyCurrentAuditor,
    userData?.uid,
    auditDataStatus,
    locationsStatus,
    usersStatus,
    loaded,
    fromDateFilter,
    toDateFilter,
    lastFetchedDateRangeKey,
    failedDateRangeKey,
  ])

  return loaded
}

export default useFetchAuditsData
